import { FastField } from 'formik';
import { lazy, memo, Suspense, useMemo } from 'react';

import { Alert, BaseTextFieldProps, Grid, Stack, TextField } from '@mui/material';

import { InterviewLanguage } from '@synergetx/puppet-interview-types';
import LoadingPaper from '@components/LoadingPaper';

// Lazy: the rich-text editor and ProseMirror are only needed for info and continue questions.
const TextToHtmlTextfield = lazy(() => import('./TextToHtmlTextfield'));

interface Props {
    position: number;
    language: InterviewLanguage;
}

const ContinuePromptQuestionFields = ({ position, language }: Props) => {
    const fieldInputProps: BaseTextFieldProps = useMemo(
        () => ({
            style: { minWidth: '15rem' },
            onFocus: (e) => {
                e.target.select();
            },
            onClick: (e) => {
                e.stopPropagation();
                e.preventDefault();
            },
        }),
        [],
    );

    return (
        <Stack direction="column" spacing={1}>
            <Alert severity="info">
                Introduce a pause in the interview to allow the user to read the message. Assessor
                may choose to continue the interview, go to the previous question, or end the
                interview.
            </Alert>
            <Suspense fallback={<LoadingPaper loading />}>
                <TextToHtmlTextfield position={position} language={language} />
            </Suspense>
            <Grid container>
                <Grid
                    size={6}
                    sx={{
                        pr: 2,
                    }}
                >
                    <FastField
                        as={TextField}
                        fullWidth
                        name={`questions[${position}].localeContent.[${language}].continueButtonDescription`}
                        label="Continue Description"
                        required
                        variant="outlined"
                        style={{ marginBottom: '1rem' }}
                        placeholder="Continue button description goes here..."
                        slotProps={{ input: fieldInputProps, inputLabel: { shrink: true } }}
                    />
                </Grid>
                <Grid size={6}>
                    <FastField
                        as={TextField}
                        fullWidth
                        name={`questions[${position}].localeContent.[${language}].continueButtonTitle`}
                        label="Continue Title"
                        required
                        variant="outlined"
                        style={{ marginBottom: '1rem' }}
                        placeholder="Continue button title goes here..."
                        slotProps={{ input: fieldInputProps, inputLabel: { shrink: true } }}
                    />
                </Grid>
                <Grid
                    size={6}
                    sx={{
                        pr: 2,
                    }}
                >
                    <FastField
                        as={TextField}
                        fullWidth
                        name={`questions[${position}].localeContent.[${language}].previousButtonDescription`}
                        label="Previous Description"
                        required
                        variant="outlined"
                        style={{ marginBottom: '1rem' }}
                        placeholder="Previous button description goes here..."
                        slotProps={{ input: fieldInputProps, inputLabel: { shrink: true } }}
                    />
                </Grid>
                <Grid size={6}>
                    <FastField
                        as={TextField}
                        fullWidth
                        name={`questions[${position}].localeContent.[${language}].previousButtonTitle`}
                        label="Previous Title"
                        required
                        variant="outlined"
                        style={{ marginBottom: '1rem' }}
                        placeholder="Previous button title goes here..."
                        slotProps={{ input: fieldInputProps, inputLabel: { shrink: true } }}
                    />
                </Grid>
                <Grid
                    size={6}
                    sx={{
                        pr: 2,
                    }}
                >
                    <FastField
                        as={TextField}
                        fullWidth
                        name={`questions[${position}].localeContent.[${language}].endInterviewButtonDescription`}
                        label="End Survey Description"
                        required
                        variant="outlined"
                        style={{ marginBottom: '1rem' }}
                        placeholder="End Survey button description goes here..."
                        slotProps={{ input: fieldInputProps, inputLabel: { shrink: true } }}
                    />
                </Grid>
                <Grid size={6}>
                    <FastField
                        as={TextField}
                        fullWidth
                        name={`questions[${position}].localeContent.[${language}].endInterviewButtonTitle`}
                        label="End Survey Title"
                        required
                        variant="outlined"
                        style={{ marginBottom: '1rem' }}
                        placeholder="End Survey button title goes here..."
                        slotProps={{ input: fieldInputProps, inputLabel: { shrink: true } }}
                    />
                </Grid>
            </Grid>
        </Stack>
    );
};

export default memo(ContinuePromptQuestionFields);
